/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
} from "react-hook-form";
import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import TextareaField from "../../../components/common/TextareaField";
import { OrderFormData, LocationState } from "../../../types/types";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
  useDistrictFetchData,
} from "../../../apis/locationApis";
import Section from "../../../components/common/Section";

interface SenderSectionProps {
  register: UseFormRegister<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  clearErrors: UseFormClearErrors<OrderFormData>;
  watch: any;
  useDifferentSender: boolean;
  setUseDifferentSender: (value: boolean) => void;
  control: Control<OrderFormData>;
}

const SenderSection: React.FC<SenderSectionProps> = ({
  register,
  errors,
  setValue,
  clearErrors,
  watch,
}) => {
  const [senderLocation, setSenderLocation] = React.useState<LocationState>({});
  const [searchRef, setSearchRef] = useState<string>("");
  const [districtSearchRef, setDistrictSearchRef] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<any>("manual");
  const {
    data: senderCities = [],
    refetch: fetchSenderCities,
    isPending: isCitiesFetchPending,
  } = useFetchCountryCitiesAllData(
    senderLocation?.country?.id,
    searchRef,
    selectedOption,
  );

  const handleCitySearch = (query: string) => {
    if (query?.trim().length >= 3) {
      setSearchRef(query);
      fetchSenderCities();
    }
  };

  // API data
  const { data: countries = [] } = useFetchCountriesAllData();
  const {
    data: senderDistricts = [],
    refetch: fetchSenderDistricts,
    isPending: isDistrictFetchPending,
  } = useDistrictFetchData(
    senderLocation?.city?.id,
    districtSearchRef,
    selectedOption,
  );

  const handleLocationChange = (field: keyof LocationState, value: any) => {
    if (!value?.id) return;

    setSenderLocation((prev) => {
      const newLocation = { ...prev, [field]: value };

      if (field === "country") {
        newLocation.city = undefined;
        newLocation.district = undefined;
        setValue("sender.city", "");
        setValue("sender.district", "");
      } else if (field === "city") {
        newLocation.district = undefined;
        setValue("sender.district", "");
      }

      return newLocation;
    });

    setValue(`sender.${field}`, value.id);
    setValue(`sender.${field}Name`, value.name);
    setValue("sender.dialCode", value?.dialCode);

    if (field === "country" && value.id) {
      fetchSenderCities();
    } else if (field === "city" && value.id) {
      fetchSenderDistricts();
    }
  };

  const handleCityChange = useCallback(
    (val: string) => {
      const city = senderCities?.find((c: any) => c.id === val);
      if (!city) return;

      setValue("sender.city", city.id);
      setValue("sender.cityName", city.name);
      setSenderLocation((prev) => ({ ...prev, city, district: undefined }));
      setValue("sender.district", "");

      if (city.id) {
        fetchSenderDistricts();
      }
    },
    [senderCities, setValue, fetchSenderDistricts],
  );

  const handleDistrictSearch = (query: string) => {
    if (query.trim().length >= 3) {
      setDistrictSearchRef(query);
      fetchSenderDistricts();
    }
  };

  const handleDistrictChange = useCallback(
    (val: string) => {
      const district = senderDistricts.find((d: any) => d.id === val);
      if (!district) return;

      setValue("sender.district", district.id);
      setValue("sender.districtName", district.name);
      setSenderLocation((prev) => ({ ...prev, district }));
    },
    [senderDistricts, setValue],
  );

  return (
    <Section title="SENDER INFORMATION">
      <div className="mt-4 grid grid-cols-6 gap-2">
        {/* Contact Info */}
        <SelectInput
          name="sender.country"
          className="col-span-6"
          label="Country"
          placeholder="Select Country"
          selectOptions={countries.map((country: any) => ({
            id: country.id,
            value: country.id,
            text: country.name,
          }))}
          register={register}
          clearErrors={clearErrors}
          validation={{ required: "Sender country is required" }}
          disabled={!countries.length}
          value={senderLocation.country?.id}
          setValue={(val: string) => {
            const country = countries.find((c: any) => c.id === val);
            handleLocationChange("country", country);
          }}
        />

        <Input
          name="sender.name"
          type="text"
          title="Name"
          register={register}
          error={errors.sender?.name}
          validation={{ required: "Sender name is required" }}
          className="col-span-2"
        />

        <div className="col-span-2">
          <label className="text-[#1E293B] text-sm whitespace-nowrap">
            Mobile<span className="text-red-600 ml-1">*</span>
          </label>
          <div className="flex items-center border border-[#D8D8D8] rounded-full bg-white px-2 mt-1">

            <span className="pr-1 text-gray-700 text-sm whitespace-nowrap">
              {watch("sender.dialCode")
                ? `+${watch("sender.dialCode")}`
                : "+--"}
            </span>
            <div className="flex-1 border-l-2">
              <Input
                name="sender.mobile"
                type="number"
                register={register}
                error={errors.sender?.mobile}
                validation={{
                  required: "Mobile number is required",
                  pattern: {
                    value: /^\d{9,10}$/,
                    message: "Mobile number must be 9 or 10 digits",
                  },
                }}
                other={{
                  maxLength: 10,
                  inputMode: "numeric",
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    let value = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setValue("sender.mobile", value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  },
                }}
                className="col-span-2"
                inputClassName="w-full border-0 outline-none bg-transparent px-2 py-[6px] text-[#1E1E1E] hide-spinner"
              />
            </div>
          </div>
          {errors.sender?.mobile && (
            <span className="text-red-500 text-sm block pl-3">
              {errors?.sender?.mobile.message}
            </span>
          )}
        </div>

        <Input
          name="sender.email"
          type="text"
          title="Email"
          register={register}
          error={errors.sender?.email}
          validation={{
            required: "Sender email is required",
            setValueAs: (value: string) => value?.toLowerCase(),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address",
            },
          }}
          className="col-span-2"
        />
        <Input
          name="sender.iqamaNo"
          type="number"
          title="Iqama Number"
          register={register}
          error={errors.sender?.iqamaNo}
          validation={{
              required: "Iqama Number is required",
              validate: (value: string) => {
                if (!/^\d+$/.test(value)) return "Only digits are allowed";

                if (value.length !== 10) {
                  return "Iqama Number must be exactly 10 digits";
                }

                return true;
              },
            }}
            other={{
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value.length > 10) {
                  value = value.slice(0, 10);
                }
                setValue("sender.iqamaNo", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              },
            }}
          className="col-span-2"
        />

        {/* Location Selects */}
        <SelectInput
          name="sender.city"
          label="City"
          className="col-span-2"
          placeholder="Select City"
          selectOptions={senderCities.map((city: any) => ({
            id: city.id,
            value: city.id,
            text: city.name,
          }))}
          clearErrors={clearErrors}
          register={register}
          validation={{ required: "Sender city is required" }}
          value={senderLocation.city?.id}
          setValue={handleCityChange}
          onSearch={handleCitySearch}
          isPending={isCitiesFetchPending}
        />

        <SelectInput
          name="sender.district"
          label="District"
          className="col-span-2"
          placeholder="Select District"
          selectOptions={senderDistricts.map((district: any) => ({
            id: district.id,
            value: district.id,
            text: district.name,
          }))}
          clearErrors={clearErrors}
          register={register}
          validation={{ required: "Sender district is required" }}
          value={senderLocation.district?.id}
          setValue={handleDistrictChange}
          onSearch={handleDistrictSearch}
          isPending={isDistrictFetchPending}
        />

        {/* Address Info */}
        <Input
          name="sender.zipCode"
          type="number"
          title="Zip Code"
          register={register}
          error={errors.sender?.zipCode}
          validation={{ required: "Sender zipcode is required" }}
          className="col-span-2"
        />

        <Input
          name="sender.latitude"
          type="number"
          title="Latitude"
          register={register}
          error={errors.sender?.latitude}
          className="col-span-2"
        />

        <Input
          name="sender.longitude"
          type="number"
          title="Longitude"
          register={register}
          error={errors.sender?.longitude}
          className="col-span-2"
        />

        <TextareaField
          rows={2}
          className="col-span-3"
          label="Address"
          register={register}
          name="sender.address"
          placeholder=""
          error={errors.sender?.address}
          validation={{ required: "Sender address is required" }}
        />

        <TextareaField
          rows={2}
          className="col-span-3"
          label="National Address"
          register={register}
          placeholder=""
          name="sender.nationalAddress"
          error={errors.sender?.nationalAddress}
          validation={{ required: "Sender national address is required" }}
        />
      </div>
    </Section>
  );
};

export default SenderSection;
